import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { formatToDDMonYYYY } from './dateUtils'; // Import date formatting utility
import './CallLogDataExport.css';

const CallLogDataExport = ({ currentUser }) => {
  const [callLogs, setCallLogs] = useState([]);
  const [assets, setAssets] = useState([]); // Master database assets
  const [message, setMessage] = useState({ text: '', type: '' });
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [filteredLogs, setFilteredLogs] = useState([]);
  // Filter states for Date Range and Attendant
  const [dateRangeFilter, setDateRangeFilter] = useState({
    startDate: '',
    endDate: ''
  });
  const [attendantFilter, setAttendantFilter] = useState('');
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100); // Default to 100 items per page
  
  // State for editing call logs
  const [editingLogId, setEditingLogId] = useState(null);
  const [editLogData, setEditLogData] = useState({});

  useEffect(() => {
    loadCallLogs();
    loadAssets(); // Load master database assets
  }, []);

  const loadCallLogs = async () => {
    let logs = [];
    try {
      // Try API first
      const response = await fetch('/api/call-logs');
      if (response.ok) {
        const payload = await response.json();
        logs = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
        console.log('âœ… Loaded', logs.length, 'call logs from API');
      } else {
        throw new Error('API call failed');
      }
    } catch (error) {
      console.warn('API unavailable for call logs:', error);
      logs = [];
      showMessage('Unable to load call logs from server. Please retry.', 'error');
    }
    
    logs = Array.isArray(logs) ? logs : [];
    const sortedLogs = sortLogsLatestFirst(logs);
    setCallLogs(sortedLogs);
    setFilteredLogs(sortedLogs);
    
    // Set default date range based on actual log data
    if (logs.length > 0) {
      const validDates = logs
        .map(log => parseLogDate(log.date))
        .filter(date => date !== null);
      
      if (validDates.length > 0) {
        const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
        
        setDateRange({
          startDate: minDate.toISOString().split('T')[0],
          endDate: maxDate.toISOString().split('T')[0]
        });
        
        // Also set the filter range to show all data by default
        setDateRangeFilter({
          startDate: '',
          endDate: ''
        });
        
        console.log('📅 Default date range set to data range:', minDate.toDateString(), 'to', maxDate.toDateString());
      } else {
        // Fallback to last 30 days if no valid dates
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - 30);
        
        setDateRange({
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0]
        });
      }
    } else {
      // No logs - set to last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      setDateRange({
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0]
      });
    }
  };

  // Load master database assets
  const loadAssets = async () => {
    let assets = [];
    try {
      // Try API first
      const response = await fetch('/api/assets');
      if (response.ok) {
        const payload = await response.json();
        assets = Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);
        console.log('âœ… Loaded', assets.length, 'assets from API');
      } else {
        throw new Error('API call failed');
      }
    } catch (error) {
      console.warn('API unavailable for assets:', error);
      assets = [];
      showMessage('Unable to load assets from server. Please retry.', 'error');
    }
    assets = Array.isArray(assets) ? assets : [];
    setAssets(assets);
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Helper function to parse DD-Mon-YYYY format dates
  const parseLogDate = (dateString) => {
    if (!dateString) return null;
    
    // Handle if it's already a Date object
    if (dateString instanceof Date) {
      return isNaN(dateString.getTime()) ? null : dateString;
    }
    
    if (typeof dateString !== 'string') return null;
    
    const str = dateString.trim();
    
    // Handle DD-Mon-YYYY or DD-Mon-YY format (e.g., 25-Dec-2023 or 25-Dec-23)
    const ddMonYyyyMatch = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
    if (ddMonYyyyMatch) {
      const day = parseInt(ddMonYyyyMatch[1], 10);
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                         'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthAbbr = ddMonYyyyMatch[2].charAt(0).toUpperCase() + ddMonYyyyMatch[2].slice(1).toLowerCase();
      const month = monthNames.indexOf(monthAbbr);
      let year = parseInt(ddMonYyyyMatch[3], 10);
      
      // Handle 2-digit years
      if (year < 100) {
        year = year < 50 ? 2000 + year : 1900 + year;
      }
      
      if (month !== -1 && day >= 1 && day <= 31) {
        return new Date(year, month, day);
      }
    }
    
    // Handle YYYY-MM-DD format (ISO format)
    const isoMatch = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const year = parseInt(isoMatch[1], 10);
      const month = parseInt(isoMatch[2], 10) - 1; // 0-based month
      const day = parseInt(isoMatch[3], 10);
      return new Date(year, month, day);
    }
    
    // Fallback to native Date parsing
    const parsed = new Date(str);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

    const parseTimeToMinutes = (timeString) => {
      if (!timeString || typeof timeString !== 'string') return 0;
      const match = timeString.trim().match(/^(\d{1,2}):(\d{2})$/);
      if (!match) return 0;
      const hours = Number(match[1]);
      const minutes = Number(match[2]);
      if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;
      return (hours * 60) + minutes;
    };

    const getLogTimestamp = (log) => {
      const parsedDate = parseLogDate(log?.date);
      if (!parsedDate) return 0;
      const totalMinutes = parseTimeToMinutes(log?.startTime || log?.callAttendTime || log?.endTime || '00:00');
      const withTime = new Date(parsedDate);
      withTime.setHours(Math.floor(totalMinutes / 60), totalMinutes % 60, 0, 0);
      return withTime.getTime();
    };

    const sortLogsLatestFirst = (logs) => {
      if (!Array.isArray(logs)) return [];
      return [...logs].sort((a, b) => getLogTimestamp(b) - getLogTimestamp(a));
    };

  // Filter logs by date range and other filters
  const filterByDateRange = () => {
    let filtered = [...callLogs]; // Create a copy to avoid mutation
    
    console.log('========================================');
    console.log('🔍 STARTING FILTER DEBUG');
    console.log('========================================');
    console.log('📊 Total logs in memory:', callLogs.length);
    console.log('📅 Date range filter:', dateRangeFilter);
    console.log('👤 Attendant filter:', attendantFilter);
    
    // Debug: Log ALL log dates to see their format
    if (callLogs.length > 0) {
      console.log('📋 ALL log dates in data:');
      callLogs.forEach((log, index) => {
        const parsed = parseLogDate(log.date);
        console.log(`  ${index + 1}. ID:${log.id} | Raw:"${log.date}" | Parsed:${parsed ? parsed.toDateString() : 'INVALID'}`);
      });
      
      // Find min and max dates in data
      const validDates = callLogs
        .map(log => parseLogDate(log.date))
        .filter(date => date !== null);
      if (validDates.length > 0) {
        const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
        const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
        console.log('📅 Data date range:', minDate.toDateString(), 'to', maxDate.toDateString());
      }
    }
    
    // Apply date range filter (from filter controls)
    if (dateRangeFilter.startDate && dateRangeFilter.endDate) {
      // Create dates at midnight local time for accurate comparison
      const filterStartDate = new Date(dateRangeFilter.startDate + 'T00:00:00');
      const filterEndDate = new Date(dateRangeFilter.endDate + 'T23:59:59.999');
      
      console.log('Filtering by date range:', filterStartDate, 'to', filterEndDate);
      
      filtered = filtered.filter(log => {
        const logDate = parseLogDate(log.date);
        if (!logDate) {
          console.log('Log has invalid date:', log.date, 'Type:', typeof log.date);
          return false; // Exclude logs with invalid dates
        }
        // Normalize log date to midnight for comparison
        const logDateNormalized = new Date(logDate.getFullYear(), logDate.getMonth(), logDate.getDate());
        const startNormalized = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), filterStartDate.getDate());
        const endNormalized = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth(), filterEndDate.getDate());
        endNormalized.setHours(23, 59, 59, 999);
        
        const match = logDateNormalized >= startNormalized && logDateNormalized <= endNormalized;
        console.log('Log date:', log.date, 'Parsed:', logDate, 'Normalized:', logDateNormalized, 'Match:', match);
        return match;
      });
    } else if (dateRangeFilter.startDate) {
      // Only start date selected - filter from start date onwards
      const filterStartDate = new Date(dateRangeFilter.startDate + 'T00:00:00');
      console.log('Filtering from start date:', filterStartDate);
      
      filtered = filtered.filter(log => {
        const logDate = parseLogDate(log.date);
        if (!logDate) return false; // Exclude logs with invalid dates
        // Normalize to date only
        const logDateNormalized = new Date(logDate.getFullYear(), logDate.getMonth(), logDate.getDate());
        const startNormalized = new Date(filterStartDate.getFullYear(), filterStartDate.getMonth(), filterStartDate.getDate());
        return logDateNormalized >= startNormalized;
      });
    } else if (dateRangeFilter.endDate) {
      // Only end date selected - filter up to end date
      const filterEndDate = new Date(dateRangeFilter.endDate + 'T23:59:59.999');
      console.log('Filtering up to end date:', filterEndDate);
      
      filtered = filtered.filter(log => {
        const logDate = parseLogDate(log.date);
        if (!logDate) return false; // Exclude logs with invalid dates
        // Normalize to date only
        const logDateNormalized = new Date(logDate.getFullYear(), logDate.getMonth(), logDate.getDate());
        const endNormalized = new Date(filterEndDate.getFullYear(), filterEndDate.getMonth(), filterEndDate.getDate());
        endNormalized.setHours(23, 59, 59, 999);
        return logDateNormalized <= endNormalized;
      });
    }
    
    // Apply attendant filter
    if (attendantFilter && attendantFilter.trim()) {
      const searchTerm = attendantFilter.trim().toLowerCase();
      console.log('Filtering by attendant. Search term:', searchTerm, 'Logs before filter:', filtered.length);
      
      // Log first few attendants to debug
      console.log('Sample attendant values in data:', filtered.slice(0, 5).map(log => log.attendant));
      
      filtered = filtered.filter(log => {
        const attendantValue = (log.attendant || '').toString().toLowerCase().trim();
        const match = attendantValue.includes(searchTerm);
        return match;
      });
      console.log('After attendant filter, results count:', filtered.length);
    }
    
    console.log('âœ… FINAL FILTERED RESULTS:', filtered.length, 'of', callLogs.length, 'logs');
    if (filtered.length === 0 && callLogs.length > 0) {
      console.warn('âš ï¸ WARNING: All logs were filtered out!');
      console.warn('   Check if your log dates fall within the selected date range.');
    }
    console.log('========================================');
    setFilteredLogs(sortLogsLatestFirst(filtered));
  };

  // Apply filters when filter values change
  useEffect(() => {
    filterByDateRange();
    setCurrentPage(1); // Reset to first page when filters change
  }, [dateRangeFilter, attendantFilter]);

  // Delete a call log entry
  const deleteCallLog = async (id) => {
    console.log('CallLogDataExport: Attempting to delete call log with ID:', id);
    if (window.confirm('Are you sure you want to delete this call log entry? This action cannot be undone.')) {
      try {
        // Try API first
        const response = await fetch(`/api/call-logs/${id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          // Refresh the logs
          loadCallLogs();
          showMessage('Call log entry deleted successfully!', 'success');
        } else {
          throw new Error('API delete failed');
        }
      } catch (error) {
        console.error('CallLogDataExport: Failed to delete call log:', error);
        showMessage('Failed to delete call log entry from server', 'error');
      }
    }
  };

  // Export condemned equipment to Excel
  const exportCondemnedEquipment = () => {
    const condemnedAssets = assets.filter(asset => asset.status === 'condemned');
    
    if (condemnedAssets.length === 0) {
      showMessage('No condemned equipment found', 'error');
      return;
    }

    // Create Excel workbook
    const headers = ['S.N', 'Department', 'Floor', 'Equipment', 'Asset ID', 'Manufacturer', 'Model', 'Serial No', 'Installation Date', 'Status Date', 'Remarks'];
    const rows = condemnedAssets.map((asset, index) => [
      index + 1,
      asset.department || '',
      asset.floor || '',
      asset.equipmentName || '',
      asset.assetId || '',
      asset.manufacturer || '',
      asset.model || '',
      asset.serialNo || '',
      asset.installationDate || '',
      asset.statusDate || '',
      asset.statusRemark || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Condemned Equipment');
    
    // Download Excel file
    XLSX.writeFile(workbook, `condemned_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    showMessage(`Exported ${condemnedAssets.length} condemned equipment records`, 'success');
  };

  // Export shifted equipment to Excel
  const exportShiftedEquipment = () => {
    const shiftedAssets = assets.filter(asset => asset.status === 'shifted');
    
    if (shiftedAssets.length === 0) {
      showMessage('No shifted equipment found', 'error');
      return;
    }

    // Create Excel workbook
    const headers = ['S.N', 'Department', 'Floor', 'Equipment', 'Asset ID', 'Manufacturer', 'Model', 'Serial No', 'Installation Date', 'Status Date', 'Remarks'];
    const rows = shiftedAssets.map((asset, index) => [
      index + 1,
      asset.department || '',
      asset.floor || '',
      asset.equipmentName || '',
      asset.assetId || '',
      asset.manufacturer || '',
      asset.model || '',
      asset.serialNo || '',
      asset.installationDate || '',
      asset.statusDate || '',
      asset.statusRemark || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Shifted Equipment');
    
    // Download Excel file
    XLSX.writeFile(workbook, `shifted_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    showMessage(`Exported ${shiftedAssets.length} shifted equipment records`, 'success');
  };

  // Export biomedical equipment to Excel
  const exportBiomedicalEquipment = () => {
    const biomedicalAssets = assets.filter(asset => asset.status === 'biomedical');
    
    if (biomedicalAssets.length === 0) {
      showMessage('No biomedical equipment found', 'error');
      return;
    }

    // Create Excel workbook
    const headers = ['S.N', 'Department', 'Floor', 'Equipment', 'Asset ID', 'Manufacturer', 'Model', 'Serial No', 'Installation Date', 'Status Date', 'Remarks'];
    const rows = biomedicalAssets.map((asset, index) => [
      index + 1,
      asset.department || '',
      asset.floor || '',
      asset.equipmentName || '',
      asset.assetId || '',
      asset.manufacturer || '',
      asset.model || '',
      asset.serialNo || '',
      asset.installationDate || '',
      asset.statusDate || '',
      asset.statusRemark || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Biomedical Equipment');
    
    // Download Excel file
    XLSX.writeFile(workbook, `biomedical_equipment_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    showMessage(`Exported ${biomedicalAssets.length} biomedical equipment records`, 'success');
  };

  // Export call logs to Excel with DD-Mon-YYYY date format
  const exportCallLogsToExcel = () => {
    const logsToExport = filteredLogs.length > 0 ? filteredLogs : callLogs;
    
    if (logsToExport.length === 0) {
      showMessage('No call logs found to export', 'error');
      return;
    }

    // Create Excel workbook
    const headers = ['S.N', 'Date', 'Asset ID', 'Serial No', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant', 'Nature of Complaint', 'Action Taken', 'Spares Used', 'Spares Cost', 'Savings', 'Call Time', 'End Time', 'Call End Date', 'Call Status'];
    const rows = logsToExport.map((log, index) => [
      index + 1,
      formatToDDMonYYYY(log.date) || '',
      log.assetId || '',
      log.serialNo || '',
      log.equipmentName || '',
      log.company || '',
      log.model || '',
      log.department || '',
      log.floor || '',
      log.attendant || '',
      log.natureOfComplaint || '',
      log.actionTaken || '',
      log.sparesUsed || '',
      log.sparesCost || '',
      log.savingsAfterSparesCost || '',
      log.startTime || '',
      log.endTime || '',
      formatToDDMonYYYY(log.callEndDate) || '',
      log.callStatus || ''
    ]);

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Call Logs');
    
    // Download Excel file
    XLSX.writeFile(workbook, `call_logs_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    showMessage(`Exported ${logsToExport.length} call log records`, 'success');
  };

  // Export call logs to CSV with DD-Mon-YYYY date format
  const exportCallLogsToCSV = () => {
    const logsToExport = filteredLogs.length > 0 ? filteredLogs : callLogs;
    
    if (logsToExport.length === 0) {
      showMessage('No call logs found to export', 'error');
      return;
    }

    // Create CSV headers
    const headers = ['S.N', 'Date', 'Asset ID', 'Serial No', 'Equipment', 'Manufacturer', 'Model', 'Department', 'Floor', 'Attendant', 'Nature of Complaint', 'Action Taken', 'Spares Used', 'Spares Cost', 'Savings', 'Call Time', 'End Time', 'Call End Date', 'Call Status'];
    
    // Create CSV rows
    const rows = logsToExport.map((log, index) => [
      index + 1,
      formatToDDMonYYYY(log.date) || '',
      log.assetId || '',
      log.serialNo || '',
      log.equipmentName || '',
      log.company || '',
      log.model || '',
      log.department || '',
      log.floor || '',
      log.attendant || '',
      log.natureOfComplaint || '',
      log.actionTaken || '',
      log.sparesUsed || '',
      log.sparesCost || '',
      log.savingsAfterSparesCost || '',
      log.startTime || '',
      log.endTime || '',
      formatToDDMonYYYY(log.callEndDate) || '',
      log.callStatus || ''
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `call_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    showMessage(`Exported ${logsToExport.length} call log records to CSV`, 'success');
  };

  // Export call logs to PDF with DD-Mon-YYYY date format
  const exportCallLogsToPDF = () => {
    const logsToExport = filteredLogs.length > 0 ? filteredLogs : callLogs;
    
    if (logsToExport.length === 0) {
      showMessage('No call logs found to export', 'error');
      return;
    }

    // Create a new window for PDF content
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showMessage('Please allow popups to export PDF', 'error');
      return;
    }

    // Create HTML content for PDF
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Call Logs Export</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { text-align: center; color: #333; }
          .date { text-align: center; margin-bottom: 20px; color: #666; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 10px; }
          th { background-color: #4a5568; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #666; }
        </style>
      </head>
      <body>
        <h1>Call Logs Report</h1>
        <div class="date">Generated on: ${new Date().toLocaleString()}</div>
        <div class="date">Total Records: ${logsToExport.length}</div>
        <table>
          <thead>
            <tr>
              <th>S.N</th>
              <th>Date</th>
              <th>Asset ID</th>
              <th>Equipment</th>
              <th>Department</th>
              <th>Attendant</th>
              <th>Nature of Complaint</th>
              <th>Action Taken</th>
              <th>Call Status</th>
            </tr>
          </thead>
          <tbody>
            ${logsToExport.map((log, index) => `
              <tr>
                <td>${index + 1}</td>
                <td>${formatToDDMonYYYY(log.date) || ''}</td>
                <td>${log.assetId || ''}</td>
                <td>${log.equipmentName || ''}</td>
                <td>${log.department || ''}</td>
                <td>${log.attendant || ''}</td>
                <td>${log.natureOfComplaint || ''}</td>
                <td>${log.actionTaken || ''}</td>
                <td>${log.callStatus || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">BME Call Log System - Confidential</div>
      </body>
      </html>
    `;

    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Auto-print after content loads
    printWindow.onload = function() {
      printWindow.print();
    };
    
    showMessage(`Exported ${logsToExport.length} call log records to PDF`, 'success');
  };

  // Delete all call logs with double confirmation
  const deleteAllCallLogs = async () => {
    // First confirmation
    if (window.confirm('Are you sure you want to delete ALL call log entries? This will remove all records.')) {
      // Second confirmation with specific text to type
      const userInput = window.prompt('To confirm deletion of ALL call log entries, please type "DELETE ALL" in the box below:');
      if (userInput && userInput.toUpperCase() === 'DELETE ALL') {
        try {
          // Try API first
          const promises = callLogs.map(log => 
            fetch(`/api/call-logs/${log.id}`, { method: 'DELETE' })
          );
          
          await Promise.all(promises);
          await loadCallLogs();
          showMessage('All call log entries deleted successfully!', 'success');
        } catch (error) {
          console.error('Failed to delete all call logs:', error);
          showMessage('Failed to delete all call logs from server', 'error');
          return;
        }
      } else if (userInput !== null) {
        showMessage('Call log deletion cancelled. Text did not match.', 'error');
      }
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Change items per page
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Start editing a call log
  const startEditing = (log) => {
    setEditingLogId(log.id);
    setEditLogData({ ...log });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingLogId(null);
    setEditLogData({});
  };

  // Save edited call log
  const saveEditedLog = async () => {
    if (editingLogId === null) return;
    
    try {
      const response = await fetch(`/api/call-logs/${editingLogId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editLogData),
      });
      
      if (response.ok) {
        // Refresh the logs
        await loadCallLogs();
        
        // Reset editing state
        setEditingLogId(null);
        setEditLogData({});
        
        showMessage('Call log updated successfully!', 'success');
      } else {
        showMessage(`Failed to update call log (${response.status})`, 'error');
      }
    } catch (error) {
      console.error('Failed to update call log:', error);
      showMessage('Failed to update call log on server', 'error');
    }
  };

  // Handle edit form input changes
  const handleEditLogChange = (e) => {
    const { name, value } = e.target;
    setEditLogData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <div className="call-log-export-container">
      <div className="card">
        {currentUser && (
          <div className="user-badge">👤 Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.type === 'success' ? '✅' : '❌'} {message.text}
          </div>
        )}
        

        
        {/* Preview section moved to the top */}
        {filteredLogs.length > 0 && (
          <div className="preview-section">
            <h3>📋 Call Service Details</h3>
            
            {/* Filter Controls */}
            {(attendantFilter || dateRangeFilter.startDate || dateRangeFilter.endDate) && (
              <div className="filter-status">
                <span className="filter-badge">🔍 Filters Active:
                  {attendantFilter && ` Attendant="${attendantFilter}"`}
                  {(dateRangeFilter.startDate || dateRangeFilter.endDate) && ` Date Range`}
                </span>
                <button 
                  className="btn btn-clear-all" 
                  onClick={() => {
                    setAttendantFilter('');
                    setDateRangeFilter({ startDate: '', endDate: '' });
                  }}
                >
                  Clear All Filters
                </button>
              </div>
            )}
            <div className="filter-controls">
              <div className="filter-group date-range-group">
                <label>📅 Date Range Filter:</label>
                <div className="date-range-inputs">
                  <input
                    type="date"
                    id="date-range-start"
                    value={dateRangeFilter.startDate}
                    onChange={(e) => setDateRangeFilter(prev => ({...prev, startDate: e.target.value}))}
                    className="filter-input"
                    placeholder="Start Date"
                  />
                  <span className="date-range-separator">to</span>
                  <input
                    type="date"
                    id="date-range-end"
                    value={dateRangeFilter.endDate}
                    onChange={(e) => setDateRangeFilter(prev => ({...prev, endDate: e.target.value}))}
                    className="filter-input"
                    placeholder="End Date"
                  />
                </div>
                {(dateRangeFilter.startDate || dateRangeFilter.endDate) && (
                  <button 
                    className="btn btn-clear" 
                    onClick={() => setDateRangeFilter({ startDate: '', endDate: '' })}
                    title="Clear date range filter"
                  >❌ Clear Date Range
                  </button>
                )}
              </div>
              <div className="filter-group">
                <label htmlFor="attendant-filter">👤 Attendant Filter:</label>
                <input
                  type="text"
                  id="attendant-filter"
                  value={attendantFilter}
                  onChange={(e) => {
                    console.log('Attendant filter input changed:', e.target.value);
                    setAttendantFilter(e.target.value);
                  }}
                  placeholder="Type attendant name to filter..."
                  className="filter-input"
                  autoComplete="off"
                />
                {(attendantFilter || dateRangeFilter.startDate || dateRangeFilter.endDate) && (
                  <div className="filter-result-count" style={{
                    marginTop: '5px',
                    fontSize: '0.85rem',
                    color: '#334155',
                    fontWeight: '500'
                  }}>
                    {String.fromCodePoint(0x1F4CA)} Showing <strong>{filteredLogs.length}</strong> of {callLogs.length} records
                    {attendantFilter && <span> for &quot;{attendantFilter}&quot;</span>}
                  </div>
                )}
              </div>
            </div>
            
            <div className="pagination-controls">
              <div className="items-per-page">
                <label>Show: </label>
                <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </select>
                <span> entries per page</span>
              </div>
              <div className="pagination-info">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLogs.length)} of {filteredLogs.length} entries
              </div>
            </div>
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Asset ID</th>
                    <th>Serial No</th>
                    <th>Equipment</th>
                    <th>Manufacturer</th>
                    <th>Model</th>
                    <th>Department</th>
                    <th>Floor</th>
                    <th>Attendant</th>
                    <th>Nature of Complaint</th>
                    <th>Action Taken</th>
                    <th>Spares Used</th>
                    <th>Spares Cost</th>
                    <th>Savings</th>
                    <th>Call Time</th>
                    <th>End Time</th>
                    <th>Call End Date</th>
                    <th>Call Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLogs.map(log => (
                    editingLogId === log.id ? (
                      // Edit form row
                      <tr key={log.id}>
                        <td>
                          <input 
                            type="text" 
                            name="date" 
                            value={editLogData.date || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="assetId" 
                            value={editLogData.assetId || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="serialNo" 
                            value={editLogData.serialNo || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="equipmentName" 
                            value={editLogData.equipmentName || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="company" 
                            value={editLogData.company || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="model" 
                            value={editLogData.model || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="department" 
                            value={editLogData.department || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="floor" 
                            value={editLogData.floor || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="attendant" 
                            value={editLogData.attendant || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="natureOfComplaint" 
                            value={editLogData.natureOfComplaint || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="actionTaken" 
                            value={editLogData.actionTaken || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="sparesUsed" 
                            value={editLogData.sparesUsed || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="sparesCost" 
                            value={editLogData.sparesCost || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="savingsAfterSparesCost" 
                            value={editLogData.savingsAfterSparesCost || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="startTime" 
                            value={editLogData.startTime || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="endTime" 
                            value={editLogData.endTime || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="date" 
                            name="callEndDate" 
                            value={editLogData.callEndDate || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <input 
                            type="text" 
                            name="callStatus" 
                            value={editLogData.callStatus || ''} 
                            onChange={handleEditLogChange} 
                          />
                        </td>
                        <td>
                          <button 
                            className="btn btn-success"
                            onClick={saveEditedLog}
                            title="Save changes"
                          >
                            {String.fromCodePoint(0x1F4BE)}
                          </button>
                          <button 
                            className="btn btn-secondary"
                            onClick={cancelEditing}
                            title="Cancel editing"
                          >
                            ❌
                          </button>
                        </td>
                      </tr>
                    ) : (
                      // Regular display row
                      <tr key={log.id}>
                        <td>{formatToDDMonYYYY(log.date)}</td>
                        <td>{log.assetId}</td>
                        <td>{log.serialNo}</td>
                        <td>{log.equipmentName}</td>
                        <td>{log.company}</td>
                        <td>{log.model}</td>
                        <td>{log.department}</td>
                        <td>{log.floor}</td>
                        <td>{log.attendant}</td>
                        <td>{log.natureOfComplaint}</td>
                        <td>{log.actionTaken}</td>
                        <td>{log.sparesUsed}</td>
                        <td>{log.sparesCost}</td>
                        <td>{log.savingsAfterSparesCost || 'NA'}</td>
                        <td>{log.startTime}</td>
                        <td>{log.endTime}</td>
                        <td>{log.callEndDate ? formatToDDMonYYYY(log.callEndDate) : 'N/A'}</td>
                        <td>{log.callStatus}</td>
                        <td>
                          <button 
                            className="btn btn-primary"
                            onClick={() => startEditing(log)}
                            title="Edit this call log"
                          >
                            âœï¸
                          </button>
                          {(currentUser?.role === 'admin' || currentUser?.role === 'manager') && (
                            <button 
                              className="btn btn-danger"
                              onClick={() => {
                                console.log('Delete button clicked for log ID:', log.id);
                                deleteCallLog(log.id);
                              }}
                              title="Delete this call log"
                            >
                              {String.fromCodePoint(0x1F5D1)}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  className="btn btn-secondary"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  // Show first, last, current, and nearby pages
                  if (pageNumber === 1 || pageNumber === totalPages || 
                      (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)) {
                    return (
                      <button
                        key={pageNumber}
                        className={`btn ${currentPage === pageNumber ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => paginate(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    );
                  } else if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                    // Show ellipsis for skipped pages
                    return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                  }
                  return null;
                })}
                
                <button 
                  className="btn btn-secondary"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
        
        {/* No data message */}
        {filteredLogs.length === 0 && (
          <div className="no-data">
            <p>
              {(dateRangeFilter.startDate || dateRangeFilter.endDate) ? (
                <>
                  {String.fromCodePoint(0x1F4C5)} <strong>No date match found.</strong>
                  <br/>No call logs exist within the selected date range.
                  {callLogs.length > 0 && (
                    <span style={{color: '#666', fontSize: '0.9em'}}>
                      <br/><br/>
                      <em>{String.fromCodePoint(0x1F4A1)} Your data contains dates from {(() => {
                        const validDates = callLogs
                          .map(log => parseLogDate(log.date))
                          .filter(date => date !== null);
                        if (validDates.length === 0) return 'N/A';
                        const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
                        const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
                        return `${formatToDDMonYYYY(minDate)} to ${formatToDDMonYYYY(maxDate)}`;
                      })()}</em>
                    </span>
                  )}
                </>
              ) : (
                <>{String.fromCodePoint(0x1F4CB)} No call logs found matching your filters.</>
              )}
              {attendantFilter && <span><br/>Attendant filter: "{attendantFilter}"</span>}
              {(dateRangeFilter.startDate || dateRangeFilter.endDate) && (
                <span><br/>Date range: {dateRangeFilter.startDate || 'Any'} to {dateRangeFilter.endDate || 'Any'}</span>
              )}
              <br/><button 
                className="btn btn-clear-all" 
                onClick={() => {
                  setAttendantFilter('');
                  setDateRangeFilter({ startDate: '', endDate: '' });
                }}
                style={{marginTop: '10px'}}
              >
                Clear All Filters
              </button>
            </p>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default CallLogDataExport;



